import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../context/I18nContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiMinus, FiMail } = FiIcons;

const AccordionItem = ({ question, answer, isOpen, onClick, idx }) => {
  return (
    <div className="border-b border-zinc-100 last:border-0 overflow-hidden">
      <button
        onClick={onClick}
        className="w-full py-8 flex items-center justify-between text-left group transition-all"
      >
        <div className="flex items-baseline gap-6">
          <span className="text-[9px] font-black text-[#D4B483] tabular-nums opacity-60 mt-1">
            {(idx + 1).toString().padStart(2, '0')}
          </span>
          <h3 className={`text-xl md:text-2xl font-semibold tracking-tight transition-colors ${isOpen ? 'text-zinc-900' : 'text-zinc-500 group-hover:text-zinc-900'}`}>
            {question}
          </h3>
        </div>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isOpen ? 'bg-zinc-900 text-white rotate-180' : 'bg-zinc-50 text-zinc-400 group-hover:bg-zinc-100 group-hover:text-zinc-900'}`}>
          <SafeIcon icon={isOpen ? FiMinus : FiPlus} className="w-5 h-5" />
        </div>
      </button>
      
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="pb-10 pl-12 md:pl-16">
              <p className="text-zinc-500 leading-[1.85] text-lg font-light max-w-2xl">
                {answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  const faqSections = [
    { key: 'general', questions: ['what', 'who', 'countries', 'free', 'account'] },
    { key: 'restaurants', questions: ['search', 'contact', 'order', 'multiple', 'payments', 'logistics', 'favourites'] },
    { key: 'suppliers', questions: ['become', 'profile', 'add', 'manage', 'prices', 'find'] },
    { key: 'payments', questions: ['processed', 'receive', 'invoice', 'fail'] },
    { key: 'delivery', questions: ['who', 'own', 'collect', 'responsible'] },
    { key: 'security', questions: ['protected', 'view', 'privacy'] },
    { key: 'roadmap', questions: ['features', 'apps', 'analytics', 'languages'] }
  ];

  const handleToggle = (sectionIdx, qIdx) => {
    const combinedKey = `${sectionIdx}-${qIdx}`;
    setOpenIndex(openIndex === combinedKey ? null : combinedKey);
  };

  return (
    <div className="min-h-screen bg-[#FCFAF8] pt-48 pb-40">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Header Section */}
          <header className="mb-32 border-b border-zinc-100 pb-20">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl md:text-7xl font-medium text-zinc-900 tracking-[-0.02em] mb-10 leading-[1.1]"
            >
              {t('faq.title')}
            </motion.h1>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
              <p className="text-xl md:text-2xl text-zinc-400 font-light italic max-w-2xl">
                {t('faq.subtitle')}
              </p>
            </div>
          </header>

          {/* FAQ Sections */}
          <div className="space-y-40">
            {faqSections.map((section, sIdx) => (
              <motion.section
                key={section.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
              >
                <div className="mb-12">
                  <h2 className="text-[10px] font-black uppercase tracking-[0.4em] text-[#D4B483] mb-4">
                    {t(`faq.sections.${section.key}.title`)}
                  </h2>
                  <div className="h-px w-24 bg-zinc-100" />
                </div>

                <div className="divide-y divide-zinc-100">
                  {section.questions.map((qKey, qIdx) => (
                    <AccordionItem
                      key={qKey}
                      idx={qIdx}
                      question={t(`faq.sections.${section.key}.questions.${qKey}.q`)}
                      answer={t(`faq.sections.${section.key}.questions.${qKey}.a`)}
                      isOpen={openIndex === `${sIdx}-${qIdx}`}
                      onClick={() => handleToggle(sIdx, qIdx)}
                    />
                  ))}
                </div>
              </motion.section>
            ))}
          </div>

          {/* Contact Block */}
          <section className="mt-48 bg-zinc-900 rounded-[3rem] p-12 md:p-24 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
              <img 
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=1000" 
                className="w-full h-full object-cover" 
                alt="" 
              />
            </div>
            <div className="relative z-10 max-w-xl mx-auto">
              <div className="w-16 h-16 bg-[#D4B483] rounded-2xl flex items-center justify-center mx-auto mb-10 shadow-xl shadow-[#D4B483]/20">
                <SafeIcon icon={FiMail} className="w-8 h-8 text-[#1A2E22]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
                {t('faq.contactTitle')}
              </h2>
              <p className="text-zinc-400 text-lg font-light mb-10">
                {t('faq.contactSubtitle')}
              </p>
              <p className="text-2xl md:text-3xl font-light tracking-tight text-white">
                hello.foodrolla@gmail.com
              </p>
            </div>
          </section>

          {/* Footer Branding */}
          <footer className="mt-48 pt-24 border-t border-zinc-100">
            <div className="flex flex-col md:flex-row justify-between items-start gap-12">
              <div className="max-w-xs">
                <div className="text-xl font-bold tracking-tight text-zinc-900 flex items-center gap-2 mb-6">
                  <div className="w-7 h-7 bg-zinc-900 rounded-lg flex items-center justify-center">
                    <span className="text-white text-sm font-black">f</span>
                  </div>
                  Foodrolla
                </div>
                <p className="text-[10px] font-bold text-zinc-400 leading-relaxed uppercase tracking-widest">
                  Supporting the professional food community.
                </p>
              </div>
              <div className="text-left md:text-right">
                <p className="text-[9px] font-black uppercase tracking-[0.4em] text-zinc-300 mb-4">
                  HELP CENTER
                </p>
                <p className="text-xs text-zinc-400 font-medium">Updated July 2026</p>
              </div>
            </div>
          </footer>
        </motion.div>
      </div>
    </div>
  );
};